/**
 * 55 — THE ROOM.
 *
 * "Can we see who has logged in and listened too? Who is seeing things?"
 *
 * ── What this screen is, and what it is deliberately not ────────────────────
 *
 * It is a band knowing there is a band. Five people, four of whom will only ever
 * open this on a phone, who rehearse in a room and then go home and forget which
 * of them heard the new take. This screen is the room, after everyone's gone
 * home: Rob heard it. Carm loved it. Brad left a note at 2:41.
 *
 * It is NOT an analytics dashboard and it is NOT a manager's view. There is:
 *   · no play COUNT — `plays` holds one row per member per take, ever, so this
 *     can say "Rob heard it" and is structurally incapable of saying "Rob heard
 *     it nine times". That is not a limitation, it is the design.
 *   · no failed login — the Worker filters those out before they ever leave D1.
 *   · no IP address, no raw user agent, no league table. A sign-in DOES now show
 *     the device and the city it came from ("iPhone · Halifax") and the exact
 *     time — the plain answer to "who has logged in", never the IP or the raw
 *     user-agent string behind it. `signinDetail()` below is that guard, mechanised.
 *
 * The difference between shared awareness and surveillance is mostly a matter of
 * what you DON'T collect, and secondly of tone. Both of those are load-bearing
 * here. If you are adding a column to this screen, ask which of the two you are
 * adding it to.
 *
 * ── Copy rules (06_FRONTEND_SPEC) ───────────────────────────────────────────
 *
 * Never a UUID. Never a filename. Never an error code. Sentences a drummer would
 * say. `safeLabel()` below is that rule made mechanical: a take's label is often
 * just the name of the file it fell out of, and "Rob listened to VoiceMemo4.m4a"
 * is exactly the sort of sentence this app does not say.
 */

import {
  getActivity,
  getPresence,
  getSeenBy,
  getDashboard,
  ApiError,
  type ActivityItem,
  type Presence,
  type Dashboard,
} from './api';
import * as player from './player';
import { h, clear, fmtTime, fmtDate, relDate, initials, emptyState, skeleton } from './ui';

/* ------------------------------------------------------------------- words */

/** Anything in the last ten minutes is "here now". */
const HERE_S = 75; // heartbeat is 30s; 75s = tolerate one missed beat
/** The feed re-reads itself this often while you're looking at it. One query. */
const POLL_MS = 60_000;

/**
 * "just now", "12m ago", "3h ago", "yesterday".
 *
 * ui.ts has relDate(), and it is right for a record on a shelf — but its finest
 * grain is "today", and on this screen "today" is useless: the whole question is
 * whether Rob is around RIGHT NOW or was around this morning.
 */
function ago(unix: number | null | undefined): string {
  if (!unix) return '';
  const s = Math.floor(Date.now() / 1000) - unix;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86_400) return `${Math.round(s / 3600)}h ago`;

  const days = Math.round(s / 86_400);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'last week';
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return `${Math.round(days / 30)} months ago`;
}

/**
 * The copy rule, mechanised.
 *
 * A take's `label` is whatever we had at the time — and for a phone upload that
 * is the FILENAME, because at that moment the filename is genuinely all we know.
 * It is fine in the database and it is not fine on this screen. Same for the
 * long ids that turn up when something has gone sideways upstream.
 *
 * Returns null for anything a person didn't type, and the sentence then works
 * around its absence instead of showing it.
 */
function safeLabel(s: string | null): string | null {
  if (!s) return null;
  const t = s.trim();
  if (!t || t.length > 60) return null;
  if (/\.[a-z0-9]{2,4}$/i.test(t)) return null;        // ends in .m4a, .mp3, .wav
  if (/^[a-z]{2,4}_[a-z0-9]{8,}$/i.test(t)) return null; // tk_9c1b…, an id got in
  if (/[0-9a-f]{8}-[0-9a-f]{4}/i.test(t)) return null;   // a UUID got in
  if (/^(zoom|rec|audio|voice ?memo|new recording)[\s_-]*\d*$/i.test(t)) return null; // ZOOM0024
  return t;
}

/** "the Apr 4 take" — but never "the the Apr 4 take". */
function theThing(label: string | null): string | null {
  if (!label) return null;
  return /^(the|a|an) /i.test(label) ? label : `the ${label}`;
}

/**
 * The device + place a sign-in came from — "iPhone · Halifax" — and nothing that
 * would turn shared awareness into surveillance. The Worker sends a friendly
 * "device · city" string, but this is the belt-and-braces: never an IP address,
 * never a raw user-agent, so even a mislabelled row can't leak one onto a phone.
 */
function signinDetail(detail: string | null): string | null {
  if (!detail) return null;
  const t = detail.trim();
  if (!t || t.length > 60) return null;
  if (/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/.test(t)) return null; // IPv4 got in
  if (/[0-9a-f]{1,4}:[0-9a-f]{0,4}:[0-9a-f:]{2,}/i.test(t)) return null; // IPv6-ish
  if (/mozilla\/|applewebkit|\bgecko\b|chrome\/|safari\/|version\/\d/i.test(t)) return null; // a UA
  return t;
}

/** "Jul 13, 2:14 PM" — the exact moment of a sign-in, in words a person reads.
 *  ui.fmtDate carries the date; the clock is 12-hour with an am/pm, no seconds. */
function signinWhen(unix: number): string {
  const d = new Date(unix * 1000);
  let hr = d.getHours();
  const min = d.getMinutes();
  const ampm = hr < 12 ? 'AM' : 'PM';
  hr = hr % 12;
  if (hr === 0) hr = 12;
  return `${fmtDate(unix)}, ${hr}:${String(min).padStart(2, '0')} ${ampm}`;
}

/**
 * The whole point of the screen: a row of data becomes a thing a person said.
 *
 * These sentences are the product. They are written the way Brad would say them
 * out loud, not the way a log line reads, and every one of them survives every
 * field being null — because on a phone in a pub, some of them will be.
 */
function sentence(a: ActivityItem): string {
  const who = a.member?.name ?? '';
  const song = a.song_title;
  const label = safeLabel(a.detail);

  switch (a.kind) {
    case 'login':
      return `${who} signed in`;

    case 'play':
      return song ? `${who} listened to ${song}` : `${who} listened to something new`;

    case 'comment': {
      // The body is the one string here a human definitely typed, so it is the
      // one string we show verbatim. The sentence is the frame around it.
      if (a.at_s != null && song) return `${who} noted at ${fmtTime(a.at_s)} on ${song}`;
      if (a.at_s != null) return `${who} left a note at ${fmtTime(a.at_s)}`;
      return song ? `${who} left a note on ${song}` : `${who} left a note`;
    }

    case 'vote': {
      const what = theThing(label) ?? (song ? `a take of ${song}` : 'a take');
      const of = label && song ? ` of ${song}` : '';
      return a.value === -1 ? `${who} isn't feeling ${what}${of}` : `${who} loved ${what}${of}`;
    }

    case 'version': {
      if (!who) return song ? `A new version of ${song} landed` : 'A new version landed';
      return song ? `${who} added a version of ${song}` : `${who} added a version`;
    }

    case 'take':
      return song ? `New take of ${song}` : 'A new recording landed';
  }
}

/* --------------------------------------------------------------- the strip */

/**
 * "Who is seeing things", at a glance, and this is the bit he actually asked
 * for. Five dots. Lit if you've been in in the last ten minutes; dimmed and
 * captioned "2h ago" if you haven't.
 *
 * Presence is written by the Worker on ANY authenticated request, so simply
 * having the app open makes you present. You do not have to do anything to be
 * seen — which is the only version of this that isn't a performance review.
 */
function presenceStrip(people: Presence[]): HTMLElement {
  const strip = h('div', { class: 'presence', role: 'list', 'aria-label': 'Who has been in' });

  for (const p of people) {
    const seen = p.last_seen_at;
    const here = seen != null && Math.floor(Date.now() / 1000) - seen < HERE_S;

    const dot = h('span', { class: 'presence-dot', 'aria-hidden': 'true', text: initials(p.name) });
    dot.style.setProperty('--who', p.color);

    const when = h('span', {
      class: 'presence-when',
      text: here ? 'here now' : seen ? ago(seen) : 'not yet',
    });

    const cell = h(
      'div',
      {
        class: 'presence-cell',
        role: 'listitem',
        'data-here': here ? '1' : '0',
        // The dot alone is a colour and a letter. A screen reader gets the sentence.
        'aria-label': here
          ? `${p.name} is here now`
          : seen
            ? `${p.name} was here ${ago(seen)}`
            : `${p.name} hasn't been in yet`,
      },
      [dot, h('span', { class: 'presence-name', text: p.name }), when],
    );

    strip.appendChild(cell);
  }

  return strip;
}

/* --------------------------------------------------------- Johhny's dashboards */
/*
 * Three visuals, admin-only. NOT the room below — this is the accountability
 * view the admin explicitly asked for: who's heard what, what each person has
 * listened to, and who's actually in it. Every glyph here is a dot, a ring, or a
 * bar — no boring bar-chart-of-one-number, and no library.
 */

function dashboards(dash: Dashboard): HTMLElement {
  const wrap = h('div', { class: 'dash-wrap' });
  wrap.appendChild(
    h('div', { class: 'dash-intro' }, [
      h('h2', { class: 'dash-intro-t', text: 'Your dashboards' }),
      h('p', { class: 'dash-intro-s', text: 'Only you see these. The room below is for everyone.' }),
    ]),
  );
  wrap.appendChild(heardMatrix(dash));
  wrap.appendChild(memberListens(dash));
  wrap.appendChild(engagement(dash));
  return wrap;
}

/** Dashboard 1 — who's heard each song. A song per row, a dot per member; the
 *  dot fills redder the more they've played it, hollow if they never have. */
function heardMatrix(dash: Dashboard): HTMLElement {
  const level = (plays: number): number => (plays <= 0 ? 0 : plays <= 1 ? 1 : plays <= 3 ? 2 : 3);
  const sec = h('section', { class: 'dash dash-heard' });
  sec.appendChild(h('div', { class: 'dash-h', text: "Who's heard each song" }));

  const grid = h('div', { class: 'dhm' });
  // header row: a blank song cell, then each member's initial
  const head = h('div', { class: 'dhm-row dhm-head' }, [h('span', { class: 'dhm-song' })]);
  for (const m of dash.members) {
    head.appendChild(h('span', { class: 'dhm-hd', title: m.name, text: initials(m.name) }));
  }
  grid.appendChild(head);

  for (const s of dash.songs) {
    const row = h('div', { class: 'dhm-row' }, [h('span', { class: 'dhm-song', text: s.title })]);
    for (const m of dash.members) {
      const l = dash.songs.length ? s.listeners.find((x) => x.member_id === m.member_id) : undefined;
      const plays = l?.plays ?? 0;
      const dot = h('span', {
        class: 'dhm-dot',
        'data-level': String(level(plays)),
        title: plays ? `${m.name} — ${plays} play${plays === 1 ? '' : 's'}` : `${m.name} — not yet`,
      });
      row.appendChild(dot);
    }
    grid.appendChild(row);
  }
  sec.appendChild(grid);
  return sec;
}

/** Dashboard 2 — what each member has listened to. A ring of songs-heard/total,
 *  their play total, and their last few plays as chips. */
function memberListens(dash: Dashboard): HTMLElement {
  const total = Math.max(1, dash.song_total);
  const sec = h('section', { class: 'dash dash-members' });
  sec.appendChild(h('div', { class: 'dash-h', text: 'What each of you has heard' }));

  const list = h('div', { class: 'dml' });
  for (const m of dash.members) {
    const pct = Math.round((m.songs_heard / total) * 100);
    const ring = h('div', { class: 'dml-ring', 'aria-hidden': 'true' }, [
      h('span', { class: 'dml-ring-num', text: String(m.songs_heard) }),
    ]);
    ring.style.setProperty('--pct', `${pct}`);

    const chips = h('div', { class: 'dml-last' });
    for (const p of m.last_plays.slice(0, 3)) {
      chips.appendChild(h('span', { class: 'dml-chip', text: `${p.title} · ${relDate(p.at) || 'today'}` }));
    }
    if (m.last_plays.length === 0) chips.appendChild(h('span', { class: 'dml-chip dml-chip--none', text: 'nothing yet' }));

    list.appendChild(
      h('div', { class: 'dml-row' }, [
        ring,
        h('div', { class: 'dml-body' }, [
          h('div', { class: 'dml-name', text: m.name }),
          h('div', {
            class: 'dml-stat',
            text: `${m.songs_heard} of ${dash.song_total} songs · ${m.total_plays} play${m.total_plays === 1 ? '' : 's'}`,
          }),
          chips,
        ]),
      ]),
    );
  }
  sec.appendChild(list);
  return sec;
}

/** Dashboard 3 — who's in it. Members ranked by an engagement score (listening +
 *  notes + reactions), a bar each, the quiet ones flagged so it's obvious who to
 *  poke. */
function engagement(dash: Dashboard): HTMLElement {
  const ranked = [...dash.members].sort((a, b) => b.score - a.score);
  const max = Math.max(1, ranked[0]?.score ?? 1);
  const sec = h('section', { class: 'dash dash-engage' });
  sec.appendChild(h('div', { class: 'dash-h', text: "Who's in it" }));

  const list = h('div', { class: 'deg' });
  for (const m of ranked) {
    const quiet = m.score <= max * 0.2;
    const bar = h('div', { class: 'deg-bar' }, [h('span', { class: 'deg-fill' })]);
    (bar.firstElementChild as HTMLElement).style.width = `${Math.max(4, Math.round((m.score / max) * 100))}%`;

    const seen = m.last_seen_at ? relDate(m.last_seen_at) || 'today' : 'never';
    list.appendChild(
      h('div', { class: 'deg-row', 'data-quiet': quiet ? '1' : '0' }, [
        h('div', { class: 'deg-top' }, [
          h('span', { class: 'deg-name', text: m.name }),
          quiet ? h('span', { class: 'deg-tag', text: 'quiet' }) : null,
          h('span', { class: 'deg-seen', text: `seen ${seen}` }),
        ]),
        bar,
        h('div', {
          class: 'deg-stat',
          text: `${m.total_plays} play${m.total_plays === 1 ? '' : 's'} · ${m.comments} note${m.comments === 1 ? '' : 's'} · ${m.reactions} reaction${m.reactions === 1 ? '' : 's'}`,
        }),
      ]),
    );
  }
  sec.appendChild(list);
  return sec;
}

/* ---------------------------------------------------------------- the feed */

function go(hash: string): void {
  // main.ts owns routing and listens for hashchange. Setting the hash is the
  // entire navigation API, which is also why a link still works with no JS
  // running and why the back button behaves.
  if (location.hash === hash) return;
  location.hash = hash;
}

function feedRow(a: ActivityItem): HTMLElement {
  const tappable = !!a.song_id;

  const dot = h('span', { class: 'feed-dot', 'aria-hidden': 'true' });
  dot.style.setProperty('--who', a.member?.color ?? '#666');

  const lines: (Node | null)[] = [
    h('div', { class: 'feed-said', text: sentence(a) }),
  ];

  // A note is the only thing here that has words of its own, and they are the
  // best words on the screen. Show them.
  if (a.kind === 'comment' && a.detail) {
    lines.push(h('div', { class: 'feed-quote', text: a.detail }));
  }

  // A sign-in says where from and exactly when — the accountability the band
  // asked for ("who has logged in"), warm not surveillant: device + place and
  // the absolute moment, in dim mono. If there's no device/place, just the time.
  if (a.kind === 'login') {
    const place = signinDetail(a.detail);
    const when = signinWhen(a.at);
    lines.push(h('div', { class: 'feed-signin', text: place ? `${place} · ${when}` : when }));
  }

  lines.push(h('div', { class: 'feed-when', text: ago(a.at) }));

  const row = h(
    tappable ? 'button' : 'div',
    {
      class: 'feed-row',
      'data-kind': a.kind,
      'data-tappable': tappable ? '1' : '0',
    },
    [dot, h('div', { class: 'feed-body' }, lines)],
  );

  if (!tappable) return h('li', {}, [row]);

  row.addEventListener('click', () => {
    // A note that knows where it is takes you THERE — to the song, at 2:41,
    // playing. That one tap is the whole reason anybody leaves a note.
    if (a.kind === 'comment' && a.at_s != null && a.version_id && a.song_id) {
      void player.play({
        versionId: a.version_id,
        songId: a.song_id,
        title: a.song_title ?? 'This one',
        subtitle: `Note at ${fmtTime(a.at_s)}`,
        seekTo: a.at_s,
      });
    }
    go(`#/song/${encodeURIComponent(a.song_id!)}`);
  });

  return h('li', {}, [row]);
}

/* -------------------------------------------------------------------- mount */

/**
 * The screen. Returns its own teardown, so whoever mounts it — a tab, a sheet,
 * a route in main.ts — can throw it away without leaking a timer or landing a
 * late response in a dead DOM.
 */
export function mountActivity(root: HTMLElement): () => void {
  const abort = new AbortController();
  let timer = 0;
  let dead = false;

  const body = h('div', { class: 'activity' });
  root.appendChild(body);

  async function load(first: boolean): Promise<void> {
    if (dead) return;
    if (first) {
      clear(body);
      body.appendChild(skeleton(4));
    }

    let people: Presence[];
    let items: ActivityItem[];
    let dash: Dashboard;
    try {
      // One round trip each, in parallel. The dashboard is admin-only server-side
      // and comes back empty for everyone else, so fetching it always is safe —
      // the charts simply don't render without data.
      [people, items, dash] = await Promise.all([
        getPresence(abort.signal),
        getActivity(60, abort.signal),
        getDashboard(abort.signal),
      ]);
    } catch (e) {
      if (dead || abort.signal.aborted) return;
      // A quiet failure on a background poll must never blow away a screen that
      // is already showing something true. Only the first load gets to complain.
      if (!first) return;
      clear(body);
      const retry = h('button', { class: 'btn-sm primary', text: 'Try again' });
      retry.addEventListener('click', () => void load(true));
      body.appendChild(
        emptyState(
          "Couldn't reach the vault",
          e instanceof ApiError ? e.human : 'Couldn’t reach the vault. Try again?',
          retry,
        ),
      );
      return;
    }

    if (dead || abort.signal.aborted) return;

    clear(body);
    if (people.length) body.appendChild(presenceStrip(people));

    // Johhny's private dashboards, above the shared room feed. Only ever
    // populated for the admin (the endpoint is admin-gated), so the band's feed
    // below stays the surveillance-free room it's meant to be.
    if (dash.members.length) body.appendChild(dashboards(dash));

    if (items.length === 0) {
      body.appendChild(
        emptyState(
          "Nothing's happened yet",
          'Go make some noise. Record something, and this is where the band will see it.',
        ),
      );
      return;
    }

    const ul = h('ul', { class: 'feed' });
    for (const a of items) ul.appendChild(feedRow(a));
    body.appendChild(ul);
  }

  void load(true);

  // Poll gently. It is one D1 query and it is what makes the presence dots mean
  // "now" instead of "whenever you opened this". Paused when the tab is hidden:
  // a phone in a pocket must not be talking to the vault every minute.
  const tick = () => {
    if (document.visibilityState === 'visible') void load(false);
  };
  timer = window.setInterval(tick, POLL_MS);

  const onVisible = () => {
    if (document.visibilityState === 'visible') void load(false);
  };
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    dead = true;
    abort.abort();
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    body.remove();
  };
}

/* ------------------------------------------------------------- seen-by strip */

/**
 * "Brad and Carm have heard this one."
 *
 * For the song screen. Returns the element SYNCHRONOUSLY and fills it in when
 * the answer arrives — so a caller can put it straight into a render() without
 * awaiting anything, and a slow network shows an empty row instead of blocking
 * the song from appearing.
 *
 * The empty case is the interesting one. A row of nothing under a take you
 * recorded is a quiet, honest "nobody has listened to this yet" — and it turns
 * out that is a far better nudge than any notification would be.
 *
 * song.ts calls this. Do not make it async; the whole point is that it isn't.
 */
export function seenByStrip(takeId: string): HTMLElement {
  const strip = h('div', { class: 'seenby', 'aria-live': 'polite' });

  void (async () => {
    let seen;
    try {
      seen = await getSeenBy(takeId);
    } catch {
      // Nobody needs to be told that the "who heard this" strip didn't load.
      // It is a grace note, not a feature. It fails to nothing.
      return;
    }

    clear(strip);
    if (seen.length === 0) {
      strip.appendChild(h('span', { class: 'seenby-none', text: "Nobody's heard this one yet" }));
      return;
    }

    strip.appendChild(h('span', { class: 'seenby-label', text: 'Heard by' }));

    for (const s of seen) {
      const dot = h('span', {
        class: 'seenby-dot',
        title: `${s.member.name} — ${ago(s.played_at)}`,
        'aria-label': `${s.member.name} heard this ${ago(s.played_at)}`,
        text: initials(s.member.name),
      });
      dot.style.setProperty('--who', s.member.color);
      strip.appendChild(dot);
    }
  })();

  return strip;
}
